<template>
    <div class="page-contents">
        <component
            :is="globalStore.header.component"
            v-if="globalStore.header"
            :global="globalStore.global.content"
            :blok="globalStore.header"
        />
        <main>
            <component v-if="story" :is="story.content.component" :blok="story.content" />
        </main>
        <component
            :is="'Lazy' + globalStore.emailSubscription.component"
            v-if="globalStore.emailSubscription"
            :global="globalStore.global.content"
            :blok="globalStore.emailSubscription"
        />
        <component
            :is="'Lazy' + globalStore.footer.component"
            v-if="globalStore.footer"
            :global="globalStore.global.content"
            :blok="globalStore.footer"
        />
    </div>
</template>

<script setup lang="ts">
definePageMeta({
    middleware: ['underscore-redirect'],
});

import { useGlobalStore } from '@nuxt-monorepo/base/app/store/global';
import type { IShuttleAirportContent, IShuttleLocationData } from '@nuxt-monorepo/ui/types/products/ShuttleProducts';
import type { RouteLocationNormalizedLoaded } from 'vue-router';

// Constants
const config = useRuntimeConfig();
const globalStore: any = useGlobalStore();
const { locale } = useI18n();
const route: RouteLocationNormalizedLoaded = useRoute();
const airportSlug = Array.isArray(route.params.airport) ? route.params.airport[0] : route.params.airport || '';
const storyblokApi = useStoryblokApi();
const pageSlug = `${config?.public.STORYBLOK_FOLDER}/pages/v2/shuttles/${airportSlug}`;
const templateSlug = `${config?.public.STORYBLOK_FOLDER}/pages/v2/shuttles/shuttle-airport-template`;
const resolveRelations = [
    'Page.globals',
    'CoreSharedComponent.SharedComponent',
    'ImageGridAirports.Airports',
    'ImageGridAirportsV2.Airports',
    'ImageGrid.Airports',
    'Link_Section.entities',
    'PSFAirportPage.globals',
    'PSFAirportPage.AirportEntity',
    'PartnersContainer.Partners',
];

// Helpers

/** Returns true if route should be blocked by config */
function isBlockedRoute(config: any, route: RouteLocationNormalizedLoaded): boolean {
    const blocked = config?.public.STORYBLOK_ROUTE_BLOCKED;
    if (!blocked) return false;
    return blocked.split(', ').some((block: string) => route.path.includes(block));
}

/** Returns object for deep replacements in storyblok content */
function getReplacements(airportData: IShuttleAirportContent | null) {
    return {
        airportCode: airportCode ?? '',
        airportName: `${airportData?.data?.content?.content?.name} ${airportData?.data?.content?.content?.type}`,
        airportFullName: airportData?.data?.content?.content?.fullname ?? '',
        airportCity: airportData?.data?.content?.content?.city ?? '',
        airportSlug: airportSlug ?? '',
    };
}

/** Fetches a Storyblok story by slug */
async function fetchStory(slug: string) {
    return storyblokApi
        .get(`cdn/stories/${slug}`, {
            version: config.public.STORYBLOK_VERSION === 'published' ? 'published' : 'draft',
            language: locale.value,
            resolve_relations: resolveRelations,
        })
        .then((res) => res?.data?.story || null)
        .catch(() => null);
}

// ---- MAIN LOGIC ----

// Get airport content
if (airportSlug.length <= 3) {
    throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        fatal: true,
    });
}
const { data: airportData } = await useAsyncData<IShuttleAirportContent>(
    `${airportSlug.replace(/-/g, '_')}/airport-content`,
    () =>
        $privateApi('contents/pages', {
            method: 'POST',
            body: { domain: 'apr.com', slug: airportSlug.replace(/-/g, '_'), subdomain: false },
        }),
    { lazy: true }
);
provide('airportData', airportData.value);
const airportCode = airportData.value?.data?.content?.content?.code;

// Get locations data
const { data: airportLocationsDataRaw } = await useFetch<{ data: IShuttleLocationData[] }>('/api/airport-location', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: { airport: airportCode?.toLowerCase(), caller: 'apr' },
});
const shuttleLocationsList = airportLocationsDataRaw.value?.data || null;
provide('shuttleLocationsList', shuttleLocationsList);

// Load Storyblok content
const storyData = (await fetchStory(pageSlug)) || (await fetchStory(templateSlug));

const story = ref(storyData);

// Blocked route or no content → error
if (isBlockedRoute(config, route) || !story?.value || !airportData.value?.data) {
    throw createError({ statusCode: 404, statusMessage: 'Page Not Found', fatal: true });
}

// Replace placeholders in Storyblok content
const replacements = getReplacements(airportData.value);
story.value.content = deepReplacePlaceholders(story.value.content, replacements);

// Handle SEO/meta and global store
if (story?.value) {
    setSeoMeta(story.value.content?.seo, config?.public, route);
    if (story.value.content?.globals) {
        globalStore.update(story.value.content.globals);
    }
}
</script>
